import { ReactElement, ReactNode } from "react";
import { Army } from "../../domain/army";
import { Unit } from "../../domain/unit";
import { atomic } from "../atomic.css";

const constraintsListItem = atomic({
  marginBlockStart: 8,
  fontSize: 14,
  lineHeight: 1.5,
});

export function ConstraintsValidationListItem(props: { children: ReactNode }) {
  return <li className={constraintsListItem}>{props.children}</li>;
}

const infoBox = atomic({
  backgroundColor: "text",
  color: "background-primary",
  paddingInline: 24,
  paddingBlock: 20,
  lineHeight: 1.5,
});

const infoBoxHeading = atomic({
  fontFamily: "arboria",
  fontWeight: "black",
  fontStyle: "italic",
  textTransform: "uppercase",
  marginBlockEnd: 12,
});

export function InfoBox(props: { heading: string; children: ReactNode }) {
  return (
    <div className={infoBox}>
      <div className={infoBoxHeading}>{props.heading}</div>
      {props.children}
    </div>
  );
}

export function UnitConstraintsValidationBox(props: { unit: Unit }) {
  const { unit } = props;

  return (
    <InfoBox heading="Invalid">
      <ul>
        {unit.failingConditions.map((fail) => {
          const key = `${fail.keyword.id}${fail.type}`;

          switch (fail.type) {
            case "HAS_NOT_KEYWORD": {
              return (
                <ConstraintsValidationListItem key={key}>
                  The {fail.keyword.name} keyword may not be taken on a unit
                  with the {fail.conditionKeywordDef.name} keyword.
                </ConstraintsValidationListItem>
              );
            }

            case "HAS_KEYWORD": {
              return (
                <ConstraintsValidationListItem key={key}>
                  The {fail.keyword.name} keyword may only be taken on a unit
                  with the {fail.conditionKeywordDef.name} keyword.
                </ConstraintsValidationListItem>
              );
            }

            case "HAS_PROFILE": {
              return (
                <ConstraintsValidationListItem key={key}>
                  The {fail.keyword.name} keyword may only be taken on a unit
                  with the {fail.profile.name} profile.
                </ConstraintsValidationListItem>
              );
            }

            case "HAS_NOT_PROFILE": {
              return (
                <ConstraintsValidationListItem key={key}>
                  The {fail.keyword.name} keyword may not be taken on a unit
                  with the {fail.profile.name} profile.
                </ConstraintsValidationListItem>
              );
            }

            case "MAX_COUNT_IN_ARMY": {
              return (
                <ConstraintsValidationListItem key={key}>
                  The keyword {fail.keyword.name} may only be taken{" "}
                  {fail.maxCount}x per army.
                </ConstraintsValidationListItem>
              );
            }
          }
        })}
      </ul>
    </InfoBox>
  );
}

export function ArmyConstraintsValidationBox(props: { army: Army }) {
  const { army } = props;

  return (
    <InfoBox heading="Invalid">
      <ul>
        {army.failingConditions.map((condition): ReactElement => {
          switch (condition.type) {
            case "INVALID_UNITS": {
              return (
                <ConstraintsValidationListItem key={condition.type}>
                  An army may not contain any invalid units, but{" "}
                  {condition.invalidUnitsCount === 1
                    ? "one is"
                    : `${condition.invalidUnitsCount} are`}{" "}
                  present.
                </ConstraintsValidationListItem>
              );
            }

            case "ARMY_COST_ABOVE_MAX": {
              return (
                <ConstraintsValidationListItem key={condition.type}>
                  The total army cost may not exceed {condition.maxArmyCost}pts.
                </ConstraintsValidationListItem>
              );
            }

            case "ARMY_SIZE_BELOW_MIN": {
              return (
                <ConstraintsValidationListItem key={condition.type}>
                  An army may not have fewer than {condition.minArmySize} units.
                </ConstraintsValidationListItem>
              );
            }

            case "ARMY_SIZE_ABOVE_MAX": {
              return (
                <ConstraintsValidationListItem key={condition.type}>
                  An army may not have more than {condition.maxArmySize} units.
                </ConstraintsValidationListItem>
              );
            }
          }
        })}
      </ul>
    </InfoBox>
  );
}
